import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Search,
  Download,
  Print,
  Email,
  Assessment as ReportIcon,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Work,
  Inventory,
  Refresh,
  MoreVert,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ReportData {
  id: string;
  name: string;
  type: 'financial' | 'operational' | 'customer' | 'inventory' | 'performance';
  description: string;
  data: any[];
  metrics: {
    total: number;
    change: number;
    changeType: 'increase' | 'decrease';
    period: string;
  };
  createdAt: string;
  updatedAt: string;
}

const Reports: React.FC = () => {
  const [reports, setReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  // Mock data for charts and metrics
  const revenueData = useMemo(() => [
    { month: 'Jan', revenue: 45000, projects: 8 },
    { month: 'Feb', revenue: 52000, projects: 10 },
    { month: 'Mar', revenue: 48000, projects: 9 },
    { month: 'Apr', revenue: 61000, projects: 12 },
    { month: 'May', revenue: 55000, projects: 11 },
    { month: 'Jun', revenue: 67000, projects: 13 },
    { month: 'Jul', revenue: 72000, projects: 14 },
    { month: 'Aug', revenue: 68000, projects: 13 },
    { month: 'Sep', revenue: 75000, projects: 15 },
    { month: 'Oct', revenue: 82000, projects: 16 },
    { month: 'Nov', revenue: 78000, projects: 15 },
    { month: 'Dec', revenue: 85000, projects: 17 },
  ], []);

  const projectStatusData = useMemo(() => [
    { name: 'Completed', value: 45, color: '#4CAF50' },
    { name: 'In Progress', value: 25, color: '#FF9800' },
    { name: 'Scheduled', value: 20, color: '#2196F3' },
    { name: 'Cancelled', value: 10, color: '#F44336' },
  ], []);

  const customerSatisfactionData = useMemo(() => [
    { month: 'Jan', satisfaction: 4.2 },
    { month: 'Feb', satisfaction: 4.5 },
    { month: 'Mar', satisfaction: 4.3 },
    { month: 'Apr', satisfaction: 4.7 },
    { month: 'May', satisfaction: 4.6 },
    { month: 'Jun', satisfaction: 4.8 },
    { month: 'Jul', satisfaction: 4.9 },
    { month: 'Aug', satisfaction: 4.7 },
    { month: 'Sep', satisfaction: 4.8 },
    { month: 'Oct', satisfaction: 4.9 },
    { month: 'Nov', satisfaction: 4.8 },
    { month: 'Dec', satisfaction: 5.0 },
  ], []);

  const teamPerformanceData = useMemo(() => [
    { name: 'Mike Rodriguez', projects: 12, hours: 180, rating: 4.8 },
    { name: 'Lisa Thompson', projects: 10, hours: 165, rating: 4.7 },
    { name: 'Alex Chen', projects: 8, hours: 140, rating: 4.6 },
    { name: 'Sarah Wilson', projects: 9, hours: 155, rating: 4.9 },
    { name: 'David Park', projects: 7, hours: 130, rating: 4.5 },
  ], []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      const mockReports: ReportData[] = [
        {
          id: '1',
          name: 'Monthly Revenue Report',
          type: 'financial',
          description: 'Comprehensive revenue analysis for the current month',
          data: revenueData,
          metrics: {
            total: 85000,
            change: 8.2,
            changeType: 'increase',
            period: 'December 2024'
          },
          createdAt: '2024-12-01T00:00:00Z',
          updatedAt: '2024-12-01T00:00:00Z'
        },
        {
          id: '2',
          name: 'Project Status Overview',
          type: 'operational',
          description: 'Current status of all active and completed projects',
          data: projectStatusData,
          metrics: {
            total: 100,
            change: 5.0,
            changeType: 'increase',
            period: 'December 2024'
          },
          createdAt: '2024-12-01T00:00:00Z',
          updatedAt: '2024-12-01T00:00:00Z'
        },
        {
          id: '3',
          name: 'Customer Satisfaction Report',
          type: 'customer',
          description: 'Customer satisfaction ratings and feedback analysis',
          data: customerSatisfactionData,
          metrics: {
            total: 4.8,
            change: 2.1,
            changeType: 'increase',
            period: 'December 2024'
          },
          createdAt: '2024-12-01T00:00:00Z',
          updatedAt: '2024-12-01T00:00:00Z'
        },
        {
          id: '4',
          name: 'Team Performance Analysis',
          type: 'performance',
          description: 'Individual and team performance metrics',
          data: teamPerformanceData,
          metrics: {
            total: 46,
            change: 3.2,
            changeType: 'increase',
            period: 'December 2024'
          },
          createdAt: '2024-12-01T00:00:00Z',
          updatedAt: '2024-12-01T00:00:00Z'
        }
      ];
      
      setReports(mockReports);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [revenueData, projectStatusData, customerSatisfactionData, teamPerformanceData]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, report: ReportData) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(report);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const handleExportClick = () => {
    setExportDialogOpen(true);
    handleMenuClose();
  };

  const handleExportConfirm = async () => {
    if (selectedReport) {
      try {
        // Mock export - replace with actual API call
        console.log(`Exporting ${selectedReport.name} as ${exportFormat}`);
        setExportDialogOpen(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to export report');
      }
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'financial': return <AttachMoney />;
      case 'operational': return <Work />;
      case 'customer': return <People />;
      case 'inventory': return <Inventory />;
      case 'performance': return <TrendingUp />;
      default: return <ReportIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'financial': return '#4CAF50';
      case 'operational': return '#2196F3';
      case 'customer': return '#FF9800';
      case 'inventory': return '#9C27B0';
      case 'performance': return '#F44336';
      default: return '#B0B0B0';
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || report.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h4"
          sx={{
            color: '#D4AF37',
            fontWeight: 700,
          }}
        >
          📊 Reports & Analytics
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={fetchReports}
          sx={{
            background: 'linear-gradient(45deg, #D4AF37 30%, #F57F17 90%)',
            color: '#000000',
            '&:hover': {
              background: 'linear-gradient(45deg, #F57F17 30%, #D4AF37 90%)',
            },
          }}
        >
          Refresh Data
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#2D1B1B', color: '#FF6B6B', border: '1px solid #FF6B6B' }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Total Revenue</Typography>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                    ${revenueData[revenueData.length - 1].revenue.toLocaleString()}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <TrendingUp sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      +8.2% from last month
                    </Typography>
                  </Box>
                </Box>
                <AttachMoney sx={{ color: '#4CAF50', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Active Projects</Typography>
                  <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 700 }}>
                    {revenueData[revenueData.length - 1].projects}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <TrendingUp sx={{ color: '#2196F3', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#2196F3' }}>
                      +1 from last month
                    </Typography>
                  </Box>
                </Box>
                <Work sx={{ color: '#2196F3', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Customer Rating</Typography>
                  <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                    {customerSatisfactionData[customerSatisfactionData.length - 1].satisfaction}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <TrendingUp sx={{ color: '#D4AF37', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#D4AF37' }}>
                      +0.1 from last month
                    </Typography>
                  </Box>
                </Box>
                <People sx={{ color: '#D4AF37', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Team Efficiency</Typography>
                  <Typography variant="h4" sx={{ color: '#9C27B0', fontWeight: 700 }}>
                    94%
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <TrendingUp sx={{ color: '#9C27B0', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#9C27B0' }}>
                      +2% from last month
                    </Typography>
                  </Box>
                </Box>
                <TrendingUp sx={{ color: '#9C27B0', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                Revenue & Projects Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
                  <XAxis dataKey="month" stroke="#FFFFFF" />
                  <YAxis stroke="#FFFFFF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1A1A1A', 
                      border: '1px solid #333333',
                      color: '#FFFFFF'
                    }} 
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stackId="1" 
                    stroke="#4CAF50" 
                    fill="#4CAF50" 
                    fillOpacity={0.3}
                    name="Revenue ($)"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="projects" 
                    stackId="2" 
                    stroke="#2196F3" 
                    fill="#2196F3" 
                    fillOpacity={0.3}
                    name="Projects"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                Project Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1A1A1A', 
                      border: '1px solid #333333',
                      color: '#FFFFFF'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Performance Table */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                Team Performance
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Team Member</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Projects</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Hours Worked</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Rating</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Performance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamPerformanceData.map((member, index) => (
                      <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.05)' } }}>
                        <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                          {member.name}
                        </TableCell>
                        <TableCell sx={{ color: '#FFFFFF' }}>
                          {member.projects}
                        </TableCell>
                        <TableCell sx={{ color: '#FFFFFF' }}>
                          {member.hours}h
                        </TableCell>
                        <TableCell sx={{ color: '#FFFFFF' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography sx={{ color: '#FFFFFF' }}>
                              {member.rating}/5
                            </Typography>
                            <Box sx={{ width: 60, height: 8, backgroundColor: '#333333', borderRadius: 4, overflow: 'hidden' }}>
                              <Box 
                                sx={{ 
                                  width: `${(member.rating / 5) * 100}%`,
                                  height: '100%',
                                  backgroundColor: member.rating >= 4.5 ? '#4CAF50' : member.rating >= 4.0 ? '#FF9800' : '#F44336'
                                }} 
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.rating >= 4.5 ? 'Excellent' : member.rating >= 4.0 ? 'Good' : 'Needs Improvement'}
                            size="small"
                            color={member.rating >= 4.5 ? 'success' : member.rating >= 4.0 ? 'warning' : 'error'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reports List */}
      <Card sx={{ 
        background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
        border: '1px solid #333333',
      }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ color: '#D4AF37' }}>
              Available Reports
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#D4AF37' }} />
                    </InputAdornment>
                  ),
                  sx: { color: '#FFFFFF', width: 200 }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#B0B0B0' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={typeFilter}
                  displayEmpty
                  onChange={(e) => setTypeFilter(e.target.value as string)}
                  sx={{ color: '#FFFFFF', '& .MuiSelect-icon': { color: '#D4AF37' } }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="financial">Financial</MenuItem>
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="inventory">Inventory</MenuItem>
                  <MenuItem value="performance">Performance</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            {filteredReports.map((report) => (
              <Grid item xs={12} md={6} lg={4} key={report.id}>
                <Card sx={{ 
                  background: 'linear-gradient(145deg, #2A2A2A 0%, #1A1A1A 100%)',
                  border: '1px solid #333333',
                  '&:hover': { borderColor: '#D4AF37' }
                }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: getTypeColor(report.type),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                          }}
                        >
                          {getTypeIcon(report.type)}
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                            {report.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                            {report.type} Report
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, report)}
                        sx={{ color: '#D4AF37' }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 2 }}>
                      {report.description}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                          {report.type === 'customer' ? report.metrics.total.toFixed(1) : report.metrics.total.toLocaleString()}
                        </Typography>
                        <Box display="flex" alignItems="center">
                          {report.metrics.changeType === 'increase' ? (
                            <TrendingUp sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                          ) : (
                            <TrendingDown sx={{ color: '#F44336', fontSize: 16, mr: 0.5 }} />
                          )}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: report.metrics.changeType === 'increase' ? '#4CAF50' : '#F44336' 
                            }}
                          >
                            {report.metrics.change}% from last period
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                        {report.metrics.period}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
          },
        }}
      >
        <MenuItem onClick={handleExportClick} sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}>
          <ListItemIcon>
            <Download fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>Export Report</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}>
          <ListItemIcon>
            <Print fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>Print Report</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}>
          <ListItemIcon>
            <Email fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>Email Report</ListItemText>
        </MenuItem>
      </Menu>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Export Report</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#FFFFFF', mb: 2 }}>
            Choose export format for "{selectedReport?.name}":
          </Typography>
          <FormControl fullWidth sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#B0B0B0' },
              '&:hover fieldset': { borderColor: '#FFFFFF' },
              '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
            },
            '& .MuiInputLabel-root': { color: '#B0B0B0' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
          }}>
            <InputLabel sx={{ color: '#B0B0B0' }}>Format</InputLabel>
            <Select
              value={exportFormat}
              label="Format"
              onChange={(e) => setExportFormat(e.target.value as string)}
              sx={{ color: '#FFFFFF', '& .MuiSelect-icon': { color: '#D4AF37' } }}
            >
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="excel">Excel</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setExportDialogOpen(false)} 
            sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExportConfirm} 
            autoFocus 
            sx={{ 
              color: '#D4AF37', 
              '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } 
            }}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
